const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const NEXT_PUBLIC_APP_ENV = process.env.NEXT_PUBLIC_APP_ENV;

type GtagFn = (...args: any[]) => void;
type AnalyticsPrimitive = string | number | boolean;

type MarketingEventMap = {
  select_content: { content_id: string; content_type: "platform_cta"; placement: string };
  share_demo_click: { demo_id: string; placement: string; experience: "share_demo" };
  generate_lead: { lead_source: "email_cta"; placement: string };
  outbound_click: { link_domain: string; content_id: string; placement: string };
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
let hasConfigured = false;
let lastPageViewLocation: string | null = null;

const isGaEnabled = () =>
  typeof NEXT_PUBLIC_APP_ENV === "string" &&
  NEXT_PUBLIC_APP_ENV.trim().toLowerCase() === "prod" &&
  typeof GA_MEASUREMENT_ID === "string" &&
  GA_MEASUREMENT_ID.trim().length > 0;

const shouldEnableDebugMode = () => {
  if (process.env.NODE_ENV !== "production") return true;
  if (typeof window === "undefined") return false;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
};

const safeTrim = (value: unknown, maxLength = 100) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

const safeSlug = (value: unknown, fallback: string) => {
  const normalized = safeTrim(value, 60).toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
  return normalized || fallback;
};

export const normalizeDemoId = (value: unknown) => {
  const normalized = safeTrim(value, 40).toLowerCase();
  return /^[a-z0-9_-]{1,40}$/.test(normalized) ? normalized : "demo_unspecified";
};

const getGtag = (): GtagFn | null => {
  if (typeof window === "undefined") return null;
  const existing = (window as any).gtag;
  if (typeof existing === "function") return existing as GtagFn;
  (window as any).dataLayer = (window as any).dataLayer || [];
  const stub: GtagFn = (...args) => {
    try {
      (window as any).dataLayer.push(args);
    } catch {
      // Analytics must never interrupt navigation.
    }
  };
  (window as any).gtag = stub;
  return stub;
};

const safePageTitle = (pathname: string) => {
  if (pathname === "/") return "Legaché | Home";
  const label = pathname.split("/").filter(Boolean).join(" ") || "Home";
  return `Legaché | ${label.replace(/\b\w/g, (char) => char.toUpperCase()).slice(0, 80)}`;
};

type SafeLocation = { page_location: string; page_path: string; page_title: string };

export const getSafeMarketingLocation = (urlOrPath?: string): SafeLocation => {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://legache.com";
  const fallback = typeof window !== "undefined" ? window.location.href : `${origin}/`;
  let url: URL;
  try {
    url = new URL(safeTrim(urlOrPath, 2048) || fallback, origin);
  } catch {
    url = new URL("/", origin);
  }

  const pathname = url.pathname || "/";
  const safeQuery = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const value = safeTrim(url.searchParams.get(key), 100);
    if (value) safeQuery.set(key, value);
  }
  const query = safeQuery.toString();
  const pagePath = `${pathname}${query ? `?${query}` : ""}`;
  return {
    page_location: `${origin}${pagePath}`,
    page_path: pagePath,
    page_title: safePageTitle(pathname),
  };
};

const ensureConfigured = (gtag: GtagFn, location: SafeLocation) => {
  if (hasConfigured) return;
  const measurementId = safeTrim(GA_MEASUREMENT_ID);
  if (!measurementId) return;
  try {
    gtag("js", new Date());
    gtag("config", measurementId, { send_page_view: false, ...location, site_surface: "marketing" });
    hasConfigured = true;
  } catch {
    // Analytics must never interrupt navigation.
  }
};

const sanitizeParams = (params: Record<string, unknown>) => {
  const payload: Record<string, AnalyticsPrimitive> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      const cleaned = safeTrim(value);
      if (cleaned) payload[key] = cleaned;
    } else if (typeof value === "number" && Number.isFinite(value)) {
      payload[key] = value;
    } else if (typeof value === "boolean") {
      payload[key] = value;
    }
  }
  return payload;
};

export const trackMarketingPageView = (urlOrPath?: string) => {
  if (!isGaEnabled()) return;
  const gtag = getGtag();
  if (!gtag) return;
  const location = getSafeMarketingLocation(urlOrPath);
  ensureConfigured(gtag, location);
  if (location.page_location === lastPageViewLocation) return;
  lastPageViewLocation = location.page_location;
  const payload: Record<string, unknown> = { ...location, site_surface: "marketing" };
  if (shouldEnableDebugMode()) payload.debug_mode = true;
  try {
    gtag("event", "page_view", payload);
  } catch {
    // Analytics must never interrupt navigation.
  }
};

export const trackMarketingEvent = <Name extends keyof MarketingEventMap>(
  name: Name,
  params: MarketingEventMap[Name]
) => {
  if (!isGaEnabled()) return;
  const gtag = getGtag();
  if (!gtag) return;
  const location = getSafeMarketingLocation();
  ensureConfigured(gtag, location);
  const payload: Record<string, unknown> = {
    ...sanitizeParams(params as Record<string, unknown>),
    ...location,
    site_surface: "marketing",
    transport_type: "beacon",
  };
  if (shouldEnableDebugMode()) payload.debug_mode = true;
  try {
    gtag("event", name, payload);
  } catch {
    // Analytics must never interrupt navigation.
  }
};

const placementForAnchor = (anchor: HTMLAnchorElement) =>
  safeSlug(anchor.dataset.analyticsPlacement, safeSlug(window.location.pathname, "page"));

const contentIdForPlatformLink = (anchor: HTMLAnchorElement, target: URL) => {
  const configured = safeSlug(anchor.dataset.analyticsId, "");
  if (configured) return configured;
  if (target.pathname.startsWith("/workspaces")) return "platform_workspaces";
  if (target.pathname.startsWith("/login")) return "platform_login";
  return "platform_root";
};

export const installMarketingLinkTracking = () => {
  if (typeof document === "undefined") return () => void 0;

  const onClick = (event: MouseEvent) => {
    const element = event.target instanceof Element ? event.target : null;
    const anchor = element?.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) return;

    const href = safeTrim(anchor.getAttribute("href"), 2048);
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
    const placement = placementForAnchor(anchor);

    if (href.toLowerCase().startsWith("mailto:")) {
      trackMarketingEvent("generate_lead", { lead_source: "email_cta", placement });
      return;
    }

    let target: URL;
    try {
      target = new URL(href, window.location.origin);
    } catch {
      return;
    }
    if (target.protocol !== "http:" && target.protocol !== "https:") return;

    const isPlatform = target.hostname === "cassandra.legache.com";
    if (isPlatform && /^\/share\/graph\/[^/]+/i.test(target.pathname)) {
      trackMarketingEvent("share_demo_click", {
        demo_id: normalizeDemoId(target.searchParams.get("demo")),
        placement,
        experience: "share_demo",
      });
      return;
    }
    if (isPlatform) {
      trackMarketingEvent("select_content", {
        content_id: contentIdForPlatformLink(anchor, target),
        content_type: "platform_cta",
        placement,
      });
      return;
    }
    if (target.origin !== window.location.origin) {
      trackMarketingEvent("outbound_click", {
        link_domain: safeTrim(target.hostname, 100),
        content_id: safeSlug(anchor.dataset.analyticsId, "external_link"),
        placement,
      });
    }
  };

  document.addEventListener("click", onClick, { capture: true });
  return () => document.removeEventListener("click", onClick, { capture: true });
};
